import { useEffect, useState } from "react";
import { ChevronRight, Plus, Users } from "lucide-react";
import { EmptyState, LoadingState } from "@/components/fsl/States";
import { api } from "@/lib/api";

const ROLE_ORDER = { Portiere: 0, Difensore: 1, Centrocampista: 2, Esterno: 3, Attaccante: 4 };
const ROLE_GROUP = { Portiere: "Portieri", Difensore: "Difensori", Centrocampista: "Centrocampisti", Esterno: "Esterni", Attaccante: "Attaccanti" };

export function teamLabel(tm) {
  if (!tm) return "";
  const name = tm.name || "";
  const low = name.toLowerCase();
  const mentionsClub = [tm.club?.name, tm.club?.short_name].some((c) => c && low.includes(c.toLowerCase()));
  let s = name;
  if (tm.category && !name.includes(tm.category)) s += ` · ${tm.category}`;
  if (!mentionsClub && (tm.club?.short_name || tm.club?.name)) s = `${tm.club.short_name || tm.club.name} · ${s}`;
  return s;
}

export function sortRoster(players) {
  return [...players].sort((a, b) => (a.status === "inactive") - (b.status === "inactive") || (ROLE_ORDER[a.role] ?? 9) - (ROLE_ORDER[b.role] ?? 9) || (a.shirt_number == null) - (b.shirt_number == null) || (a.shirt_number ?? 0) - (b.shirt_number ?? 0) || (a.last_name || "").localeCompare(b.last_name || "") || (a.first_name || "").localeCompare(b.first_name || ""));
}

function TeamSection({ tm, players, onOpen, onAdd, showYear }) {
  const sorted = sortRoster(players.filter((p) => p.status !== "inactive"));
  let lastGroup = null;
  return (
    <section className="fsl-card overflow-hidden" data-testid={`roster-all-team-${tm.id}`}>
      <header className="flex items-center gap-3 px-4 py-3 border-b border-white/10 bg-ink-950/40">
        {tm.club?.colors?.primary && <span className="h-6 w-1.5 rounded-full shrink-0" style={{ background: tm.club.colors.primary }} />}
        <div className="flex-1 min-w-0"><div className="font-display font-bold uppercase truncate" data-testid={`roster-all-team-name-${tm.id}`}>{teamLabel(tm)}</div><div className="text-[11px] text-fsl-slate">{sorted.length} giocatori{tm.category ? ` · categoria ${tm.category}` : ""}</div></div>
        {onAdd && <button type="button" className="btn-gold h-9" onClick={() => onAdd(tm.id)} title="Aggiungi un giocatore mancante a questa rosa" data-testid={`roster-all-add-${tm.id}`}><Plus className="h-4 w-4" /> Aggiungi</button>}
        <button type="button" className="btn-ghost h-9" onClick={() => onOpen(tm.id, tm.name)} data-testid={`roster-all-open-${tm.id}`}>Apri scheda <ChevronRight className="h-4 w-4" /></button>
      </header>
      {sorted.length === 0 ? <div className="px-4 py-3 text-xs text-fsl-slate">Rosa vuota</div> : (
        <table className="w-full table-dark text-sm"><thead><tr><th className="w-12">N.</th><th>Giocatore</th><th className="w-36">Ruolo</th>{showYear && <th className="w-16">Anno</th>}</tr></thead><tbody>
          {sorted.map((p) => {
            const g = ROLE_GROUP[p.role] || "Senza ruolo";
            const head = g !== lastGroup; lastGroup = g;
            return [
              head && <tr key={`g-${p.id}`} className="bg-fsl-gold/15"><td colSpan={showYear ? 4 : 3} className="py-1 px-3 text-[11px] font-bold uppercase tracking-wider text-fsl-gold" data-testid={`roster-all-group-${tm.id}-${g}`}>{g}</td></tr>,
              <tr key={p.id} data-testid={`roster-all-player-${p.id}`}><td className="num font-bold">{p.shirt_number ?? <span className="text-fsl-warning">—</span>}</td><td className="font-semibold">{p.last_name} {p.first_name}</td><td className="text-xs text-fsl-slate">{p.role || "—"}</td>{showYear && <td className="num text-xs">{p.birth_year || "—"}</td>}</tr>,
            ];
          })}
        </tbody></table>
      )}
    </section>
  );
}

// Vista «Tutte le squadre»: una sezione per squadra, giocatori ordinati per ruolo e numero di maglia.
export function RosterAllTeams({ tid, teams, onOpen, onAdd, showYear = true, refreshKey }) {
  const [players, setPlayers] = useState(null);
  useEffect(() => { let live = true; api.get(`/tournaments/${tid}/players`).then((r) => { if (live) setPlayers(r.data); }).catch(() => { if (live) setPlayers([]); }); return () => { live = false; }; }, [tid, refreshKey]);
  if (!players) return <LoadingState />;
  const real = teams.filter((t) => !t.placeholder && t.status !== "pending");
  if (!real.length) return <EmptyState icon={Users} title="Nessuna squadra" />;
  const byTeam = players.reduce((acc, p) => { (acc[p.team_id] = acc[p.team_id] || []).push(p); return acc; }, {});
  const cats = [...new Set(real.map((t) => t.category || ""))].sort();
  return (
    <div className="space-y-6" data-testid="roster-all-teams">
      {cats.map((cat) => (
        <div key={cat || "none"} className="space-y-3">
          {cats.length > 1 && <h3 className="fsl-section-title" data-testid={`roster-all-cat-${cat}`}>Categoria {cat || "—"}</h3>}
          <div className="grid lg:grid-cols-2 gap-4">
            {real.filter((t) => (t.category || "") === cat).sort((a, b) => teamLabel(a).localeCompare(teamLabel(b))).map((tm) => <TeamSection key={tm.id} tm={tm} players={byTeam[tm.id] || []} onOpen={onOpen} onAdd={onAdd} showYear={showYear} />)}
          </div>
        </div>
      ))}
    </div>
  );
}
